import React from 'react';
import { Course, UserRole } from '../../types/lms.types';

interface AllContentViewProps {
  course: Course;
  userRole: UserRole;
  selectedModule: string;
}

const AllContentView: React.FC<AllContentViewProps> = ({ 
  course, 
  userRole, 
  selectedModule 
}) => {
  // Mock consolidated content data
  const allContent = [
    {
      id: 1,
      type: 'assignment',
      title: 'Linear Algebra Problem Set',
      description: 'Complete exercises 1-15 from Chapter 3',
      moduleId: 'mod1',
      dueDate: '20/1/2024',
      status: 'Pending',
      icon: '📝'
    },
    {
      id: 2,
      type: 'quiz',
      title: 'Algebra Fundamentals Quiz',
      description: 'Test your understanding of basic algebra',
      moduleId: 'mod1',
      dueDate: '18/1/2024',
      status: 'Available',
      icon: '📋'
    },
    {
      id: 3,
      type: 'material',
      title: 'Lecture Notes - Chapter 1',
      description: 'Introduction to advanced mathematics concepts',
      moduleId: 'mod1',
      uploadDate: '15/1/2024',
      icon: '📁'
    },
    {
      id: 4,
      type: 'announcement',
      title: 'Welcome to Advanced Mathematics',
      description: 'Welcome everyone! This semester we will cover advanced mathematical concepts.',
      moduleId: 'all',
      date: '5/1/2024',
      icon: '📢'
    },
    {
      id: 5,
      type: 'glossary',
      title: 'Linear Equation',
      description: 'An algebraic equation in which each term is either a constant or the product of a constant and a single variable',
      moduleId: 'mod1',
      icon: '📖'
    }
  ].filter(item => selectedModule === 'all' || item.moduleId === selectedModule || item.moduleId === 'all');

  const getStatusBadge = (status: string, type: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    if (type === 'assignment' || type === 'quiz') {
      switch (status) {
        case 'Pending':
          return `${baseClasses} bg-yellow-100 text-yellow-800`;
        case 'Submitted':
          return `${baseClasses} bg-green-100 text-green-800`;
        case 'Available':
          return `${baseClasses} bg-blue-100 text-blue-800`;
        default:
          return `${baseClasses} bg-gray-100 text-gray-800`;
      }
    }
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-xl font-semibold">All Content</h2>
        <div className="text-sm text-gray-500">
          Showing {allContent.length} items
        </div>
      </div>

      <div className="space-y-4">
        {allContent.map((item) => (
          <div
            key={`${item.type}-${item.id}`}
            className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {item.title}
                    </h3>
                    <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                      {item.type}
                    </span>
                  </div>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  {item.description}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {item.dueDate && (
                    <span>Due: {item.dueDate}</span>
                  )}
                  {item.uploadDate && (
                    <span>Uploaded: {item.uploadDate}</span>
                  )}
                  {item.date && (
                    <span>Posted: {item.date}</span>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-2">
                {item.status && (
                  <span className={getStatusBadge(item.status, item.type)}>
                    {item.status}
                  </span>
                )}
                
                <button className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllContentView;
